import { Badge, Group, Select, Text } from '@mantine/core';
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { useMemo } from 'react';
import {
  getZonedWeekdayAndMinutes,
  isIntervalWithinLocationHours,
  resolveEffectiveWeeklyHours,
  type LocationWeeklyHours,
} from '@unify/fhir-model/resources/location';
import type { Room } from '../../settings/components/RoomManagementSection';
import type { MinuteInterval } from './roomSchedulingDemoData';

export function isRoomOpenForBooking(
  room: Pick<Room, 'hours' | 'status'>,
  siteHours: LocationWeeklyHours | undefined,
  start: Date,
  end: Date,
): boolean {
  if (room.status === 'inactive') return false;
  const hours = resolveEffectiveWeeklyHours(siteHours, room.hours);
  return isIntervalWithinLocationHours(start.toISOString(), end.toISOString(), hours);
}

export interface RoomSelectProps {
  /** Active rooms for this site pre-filtered to the correct type (therapy-room or phone-booth). */
  rooms: Room[];
  eventDate: Date | null;
  startTimeLabel: string;
  endTimeLabel: string;
  /** UTC instants for the requested window (display-TZ wall clock already applied). */
  bookingStart: Date | null;
  bookingEnd: Date | null;
  siteHours?: LocationWeeklyHours;
  value: string | null;
  onChange: (roomId: string | null) => void;
  busyIntervals?: Map<string, MinuteInterval[]>;
  /**
   * When set, overrides interval checks — room ids busy on any session occurrence
   * (e.g. recurring group setup).
   */
  conflictedIds?: Set<string>;
  disabled?: boolean;
  loading?: boolean;
}

export function RoomSelect({
  rooms,
  bookingStart,
  bookingEnd,
  siteHours,
  value,
  onChange,
  busyIntervals,
  conflictedIds,
  disabled,
  loading,
}: RoomSelectProps): JSX.Element | null {
  const windowReady = Boolean(bookingStart && bookingEnd && bookingEnd > bookingStart);

  const requestedWindow = useMemo(() => {
    if (!bookingStart || !bookingEnd || bookingEnd <= bookingStart) return null;
    const start = getZonedWeekdayAndMinutes(bookingStart.toISOString());
    const end = getZonedWeekdayAndMinutes(bookingEnd.toISOString());
    if (!start || !end) return null;
    let endMinutes = end.minutes;
    if (start.day !== end.day) {
      if (end.minutes !== 0) return null;
      endMinutes = 24 * 60;
    }
    if (start.minutes >= endMinutes) return null;
    return { startMinutes: start.minutes, endMinutes };
  }, [bookingStart, bookingEnd]);

  const availability = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const r of rooms) {
      if (conflictedIds?.has(r.id)) {
        map.set(r.id, false);
        continue;
      }
      if (r.status === 'inactive') {
        map.set(r.id, false);
        continue;
      }
      if (bookingStart && bookingEnd && bookingEnd > bookingStart) {
        if (!isRoomOpenForBooking(r, siteHours, bookingStart, bookingEnd)) {
          map.set(r.id, false);
          continue;
        }
      }
      const intervals = busyIntervals?.get(r.id) ?? [];
      const isBooked = requestedWindow
        ? intervals.some(
          (interval) =>
            requestedWindow.startMinutes < interval.endMinutes &&
            requestedWindow.endMinutes > interval.startMinutes,
        )
        : false;
      map.set(r.id, !isBooked);
    }
    return map;
  }, [rooms, busyIntervals, conflictedIds, requestedWindow, bookingStart, bookingEnd, siteHours]);

  const isPhoneBooth = rooms.length > 0 && rooms[0].type === 'phone-booth';
  const selectedUnavailable = Boolean(value && !(availability.get(value) ?? true));
  const selectedClosed =
    Boolean(value && bookingStart && bookingEnd)
    && rooms.some(
      (r) => r.id === value && !isRoomOpenForBooking(r, siteHours, bookingStart!, bookingEnd!),
    );

  if (rooms.length === 0) return null;

  const data = rooms.map((r) => ({
    value: r.id,
    label: r.name,
    // Keep the current value selectable so the user can clear a conflicted room.
    disabled: r.id !== value && !(availability.get(r.id) ?? true),
  }));

  return (
    <Select
      label={isPhoneBooth ? 'Phone booth' : 'Room'}
      placeholder={
        loading
          ? 'Checking availability…'
          : windowReady
            ? 'Select a room'
            : 'Set date and time first'
      }
      description={
        !windowReady
          ? 'Choose appointment date and start/end times to see availability.'
          : selectedClosed
            ? 'Selected room is closed at this time.'
            : selectedUnavailable
              ? 'Selected room is booked for one or more session times.'
              : undefined
      }
      error={
        selectedClosed
          ? 'Room is closed for the selected schedule'
          : selectedUnavailable
            ? 'Room is booked for the selected schedule'
            : undefined
      }
      data={data}
      value={value}
      onChange={onChange}
      disabled={disabled || loading || !windowReady}
      searchable
      clearable
      data-testid="room-select"
      renderOption={({ option }) => {
        const ok = availability.get(option.value) ?? true;
        const room = rooms.find((r) => r.id === option.value);
        const closed =
          Boolean(bookingStart && bookingEnd && room)
          && !isRoomOpenForBooking(room!, siteHours, bookingStart!, bookingEnd!);
        return (
          <Group gap="sm" wrap="nowrap">
            {ok ? (
              <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />
            ) : (
              <IconCircleX size={16} color="var(--mantine-color-red-6)" />
            )}
            <Text size="sm" style={{ flex: 1 }}>
              {option.label}
            </Text>
            <Badge size="xs" color={ok ? 'green' : 'red'} variant="light">
              {ok ? 'Available' : closed ? 'Closed' : 'Booked'}
            </Badge>
          </Group>
        );
      }}
    />
  );
}
